(() => {
  const initialiseGallery = (gallery) => {
    if (!gallery || gallery.dataset.galleryReady === 'true') return;
    gallery.dataset.galleryReady = 'true';

    const slides = Array.from(gallery.querySelectorAll('[data-gallery-slide]'));
    const thumbnails = Array.from(gallery.querySelectorAll('[data-gallery-thumbnail]'));
    const stage = gallery.querySelector('[data-gallery-stage]');
    const counter = gallery.querySelector('[data-gallery-counter]');
    const hint = gallery.querySelector('[data-gallery-hint]');
    const previous = gallery.querySelector('[data-gallery-previous]');
    const next = gallery.querySelector('[data-gallery-next]');
    const lightbox = gallery.querySelector('[data-product-lightbox]');
    let currentIndex = 0;
    let mainTouchStart = null;
    let suppressOpen = false;

    const pauseInactiveMedia = () => {
      slides.forEach((slide, index) => {
        if (index === currentIndex) return;
        slide.querySelectorAll('video').forEach((video) => video.pause());
      });
    };

    const showSlide = (requestedIndex) => {
      if (!slides.length) return;
      currentIndex = (requestedIndex + slides.length) % slides.length;

      slides.forEach((slide, index) => {
        const active = index === currentIndex;
        slide.hidden = !active;
        slide.classList.toggle('is-active', active);
      });

      thumbnails.forEach((thumbnail, index) => {
        const active = index === currentIndex;
        thumbnail.classList.toggle('is-active', active);
        thumbnail.setAttribute('aria-current', active ? 'true' : 'false');
        if (active) thumbnail.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      });

      if (counter) counter.textContent = `${currentIndex + 1} of ${slides.length}`;
      if (hint) hint.hidden = slides[currentIndex].dataset.mediaType !== 'image';
      pauseInactiveMedia();
    };

    thumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener('click', () => showSlide(Number(thumbnail.dataset.index)));
    });
    previous?.addEventListener('click', () => showSlide(currentIndex - 1));
    next?.addEventListener('click', () => showSlide(currentIndex + 1));

    if (stage) {
      stage.addEventListener('touchstart', (event) => {
        if (event.touches.length !== 1 || event.target.closest('video, iframe, model-viewer')) return;
        mainTouchStart = { x: event.touches[0].clientX, y: event.touches[0].clientY };
        suppressOpen = false;
      }, { passive: true });

      stage.addEventListener('touchend', (event) => {
        if (!mainTouchStart || !event.changedTouches.length) return;
        const xDifference = event.changedTouches[0].clientX - mainTouchStart.x;
        const yDifference = event.changedTouches[0].clientY - mainTouchStart.y;
        if (Math.abs(xDifference) > 50 && Math.abs(xDifference) > Math.abs(yDifference)) {
          suppressOpen = true;
          showSlide(currentIndex + (xDifference < 0 ? 1 : -1));
          window.setTimeout(() => { suppressOpen = false; }, 250);
        }
        mainTouchStart = null;
      }, { passive: true });
    }

    const precisePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (precisePointer) {
      gallery.querySelectorAll('[data-open-lightbox]').forEach((button) => {
        button.addEventListener('pointerenter', () => button.classList.add('is-zooming'));
        button.addEventListener('pointermove', (event) => {
          const bounds = button.getBoundingClientRect();
          const x = ((event.clientX - bounds.left) / bounds.width) * 100;
          const y = ((event.clientY - bounds.top) / bounds.height) * 100;
          button.style.setProperty('--zoom-x', `${x}%`);
          button.style.setProperty('--zoom-y', `${y}%`);
        });
        button.addEventListener('pointerleave', () => {
          button.classList.remove('is-zooming');
          button.style.removeProperty('--zoom-x');
          button.style.removeProperty('--zoom-y');
        });
      });
    }

    if (lightbox) {
      const image = lightbox.querySelector('[data-lightbox-image]');
      const viewport = lightbox.querySelector('[data-lightbox-viewport]');
      const lightboxCounter = lightbox.querySelector('[data-lightbox-counter]');
      const lightboxPrevious = lightbox.querySelector('[data-lightbox-previous]');
      const lightboxNext = lightbox.querySelector('[data-lightbox-next]');
      const imageSlides = slides.filter((slide) => slide.dataset.mediaType === 'image');
      const pointers = new Map();
      let lightboxIndex = 0;
      let scale = 1;
      let translateX = 0;
      let translateY = 0;
      let dragPoint = null;
      let gestureStart = null;
      let pinchStartDistance = 0;
      let pinchStartScale = 1;
      let wasPinching = false;
      let opener = null;

      const applyTransform = () => {
        image.style.transform = `translate3d(${translateX}px, ${translateY}px, 0) scale(${scale})`;
        viewport.classList.toggle('is-zoomed', scale > 1);
      };

      const resetZoom = () => {
        scale = 1;
        translateX = 0;
        translateY = 0;
        pointers.clear();
        dragPoint = null;
        wasPinching = false;
        applyTransform();
      };

      const setScale = (value) => {
        scale = Math.min(4, Math.max(1, value));
        if (scale === 1) {
          translateX = 0;
          translateY = 0;
        }
        applyTransform();
      };

      const renderLightbox = (requestedIndex) => {
        if (!imageSlides.length) return;
        lightboxIndex = (requestedIndex + imageSlides.length) % imageSlides.length;
        const sourceButton = imageSlides[lightboxIndex].querySelector('[data-open-lightbox]');
        resetZoom();
        image.src = sourceButton.dataset.fullImage;
        image.alt = sourceButton.dataset.imageAlt || '';
        if (lightboxCounter) lightboxCounter.textContent = `${lightboxIndex + 1} of ${imageSlides.length}`;
        if (lightboxPrevious) lightboxPrevious.hidden = imageSlides.length < 2;
        if (lightboxNext) lightboxNext.hidden = imageSlides.length < 2;
      };

      const openLightbox = (slide) => {
        if (suppressOpen) return;
        const requestedIndex = imageSlides.indexOf(slide);
        if (requestedIndex < 0) return;
        opener = document.activeElement;
        renderLightbox(requestedIndex);
        if (typeof lightbox.showModal === 'function') lightbox.showModal();
        else lightbox.setAttribute('open', '');
        document.documentElement.classList.add('product-lightbox-open');
      };

      gallery.querySelectorAll('[data-open-lightbox]').forEach((button) => {
        button.addEventListener('click', () => openLightbox(button.closest('[data-gallery-slide]')));
      });

      const closeLightbox = () => {
        if (typeof lightbox.close === 'function') lightbox.close();
        else lightbox.removeAttribute('open');
      };

      lightbox.querySelector('[data-lightbox-close]')?.addEventListener('click', closeLightbox);
      lightboxPrevious?.addEventListener('click', () => renderLightbox(lightboxIndex - 1));
      lightboxNext?.addEventListener('click', () => renderLightbox(lightboxIndex + 1));
      lightbox.querySelector('[data-lightbox-zoom-in]')?.addEventListener('click', () => setScale(scale + 0.5));
      lightbox.querySelector('[data-lightbox-zoom-out]')?.addEventListener('click', () => setScale(scale - 0.5));
      lightbox.querySelector('[data-lightbox-reset]')?.addEventListener('click', resetZoom);

      lightbox.addEventListener('click', (event) => {
        if (event.target === lightbox) closeLightbox();
      });
      lightbox.addEventListener('close', () => {
        document.documentElement.classList.remove('product-lightbox-open');
        resetZoom();
        if (opener instanceof HTMLElement) opener.focus();
      });
      lightbox.addEventListener('cancel', () => document.documentElement.classList.remove('product-lightbox-open'));
      lightbox.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') renderLightbox(lightboxIndex - 1);
        if (event.key === 'ArrowRight') renderLightbox(lightboxIndex + 1);
      });

      const distanceBetweenPointers = () => {
        const points = Array.from(pointers.values());
        if (points.length < 2) return 0;
        return Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
      };

      viewport?.addEventListener('pointerdown', (event) => {
        viewport.setPointerCapture?.(event.pointerId);
        pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        if (pointers.size === 1) {
          gestureStart = { x: event.clientX, y: event.clientY };
          dragPoint = { x: event.clientX, y: event.clientY };
          wasPinching = false;
        } else if (pointers.size === 2) {
          pinchStartDistance = distanceBetweenPointers();
          pinchStartScale = scale;
          wasPinching = true;
        }
      });

      viewport?.addEventListener('pointermove', (event) => {
        if (!pointers.has(event.pointerId)) return;
        pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        if (pointers.size === 2) {
          const distance = distanceBetweenPointers();
          if (pinchStartDistance) setScale(pinchStartScale * (distance / pinchStartDistance));
        } else if (pointers.size === 1 && scale > 1 && dragPoint) {
          translateX += event.clientX - dragPoint.x;
          translateY += event.clientY - dragPoint.y;
          dragPoint = { x: event.clientX, y: event.clientY };
          applyTransform();
        }
      });

      const finishPointer = (event) => {
        const point = pointers.get(event.pointerId);
        if (pointers.size === 1 && point && gestureStart && scale === 1 && !wasPinching) {
          const xDifference = point.x - gestureStart.x;
          const yDifference = point.y - gestureStart.y;
          if (Math.abs(xDifference) > 60 && Math.abs(xDifference) > Math.abs(yDifference)) {
            renderLightbox(lightboxIndex + (xDifference < 0 ? 1 : -1));
          }
        }
        pointers.delete(event.pointerId);
        dragPoint = pointers.size === 1 ? Array.from(pointers.values())[0] : null;
        if (pointers.size < 2) pinchStartDistance = 0;
        if (!pointers.size) gestureStart = null;
      };

      viewport?.addEventListener('pointerup', finishPointer);
      viewport?.addEventListener('pointercancel', finishPointer);
      viewport?.addEventListener('wheel', (event) => {
        event.preventDefault();
        setScale(scale + (event.deltaY < 0 ? 0.25 : -0.25));
      }, { passive: false });
    }

    const productPage = gallery.closest('.product-page');
    const variantSelect = productPage?.querySelector('[data-product-variant]');
    const productPrice = productPage?.querySelector('[data-product-price]');
    const mobilePrice = productPage?.querySelector('[data-mobile-price]');
    const addButton = productPage?.querySelector('[data-product-add]');
    const mobileAddButton = productPage?.querySelector('[data-mobile-add]');
    const productForm = productPage?.querySelector('.product-form');
    const quantityInput = productPage?.querySelector('[data-product-quantity]');
    const quantityMessage = productPage?.querySelector('[data-quantity-message]');

    const stockStatus = productPage?.querySelector('[data-stock-status]');
    const stockStatusText = stockStatus?.querySelector('[data-stock-status-text]');

    const selectedVariantData = () => variantSelect?.tagName === 'SELECT'
      ? variantSelect.selectedOptions[0]
      : variantSelect;

    const clearQuantityMessage = () => {
      if (!quantityMessage) return;
      quantityMessage.textContent = '';
      quantityMessage.hidden = true;
      quantityInput?.removeAttribute('aria-invalid');
      quantityInput?.setCustomValidity('');
    };

    const showQuantityMessage = (message) => {
      if (!quantityMessage || !message) return;
      quantityMessage.textContent = message;
      quantityMessage.hidden = false;
      quantityInput?.setAttribute('aria-invalid', 'true');
      quantityInput?.focus();
    };

    const refreshPurchaseState = () => {
      const option = selectedVariantData();
      const available = option?.dataset.available === 'true';
      if (stockStatus && stockStatusText) {
        const inStock = available && option?.dataset.inStock === 'true';
        stockStatus.dataset.state = !available ? 'sold-out' : inStock ? 'in-stock' : 'available-to-order';
        stockStatusText.textContent = !available
          ? stockStatus.dataset.soldOutLabel
          : inStock ? stockStatus.dataset.inStockLabel : stockStatus.dataset.availableLabel;
      }


      if (quantityInput) {
        quantityInput.disabled = !available;
        quantityInput.removeAttribute('max');
        if (Number(quantityInput.value) < 1) quantityInput.value = '1';
      }

      [addButton, mobileAddButton].forEach((button) => {
        if (!button) return;
        button.disabled = !available;
        button.textContent = available ? quantityMessage?.dataset.addLabel : quantityMessage?.dataset.soldOutLabel;
      });
      clearQuantityMessage();
    };

    variantSelect?.addEventListener('change', () => {
      const option = selectedVariantData();
      if (option?.dataset.price) {
        if (productPrice) productPrice.textContent = option.dataset.price;
        if (mobilePrice) mobilePrice.textContent = option.dataset.price;
      }
      refreshPurchaseState();
      if (option?.dataset.mediaId) {
        const mediaIndex = slides.findIndex((slide) => slide.dataset.mediaId === option.dataset.mediaId);
        if (mediaIndex >= 0) showSlide(mediaIndex);
      }
    });

    quantityInput?.addEventListener('input', clearQuantityMessage);
    productForm?.addEventListener('submit', (event) => {
      const option = selectedVariantData();
      const productRemaining = Math.max(0, Number(option?.dataset.productRemaining || 0));
      const stockRemaining = Math.max(0, Number(option?.dataset.stockRemaining || 0));
      const inventoryTracked = option?.dataset.inventoryTracked === 'true';
      const requested = Number(quantityInput?.value || 1);
      let message = '';

      if (requested < 1) message = quantityMessage?.dataset.invalidMessage || '';
      else if (requested > productRemaining) message = quantityMessage?.dataset.limitMessage || '';
      else if (inventoryTracked && requested > stockRemaining) {
        message = stockRemaining > 0
          ? (quantityMessage?.dataset.stockMessage || '').replace('COUNT_TOKEN', String(stockRemaining))
          : quantityMessage?.dataset.stockNoneMessage || '';
      }

      if (message) {
        event.preventDefault();
        showQuantityMessage(message);
      }
    });

    mobileAddButton?.addEventListener('click', () => {
      if (!mobileAddButton.disabled) productForm?.requestSubmit();
    });

    refreshPurchaseState();
    showSlide(0);
  };

  const initialiseAll = (scope = document) => {
    scope.querySelectorAll('[data-product-gallery]').forEach(initialiseGallery);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialiseAll());
  else initialiseAll();

  document.addEventListener('shopify:section:load', (event) => initialiseAll(event.target));
})();

(() => {
  const moveReviewsBeforeRecommendations = () => {
    const reviewWidget = document.getElementById('judgeme_product_reviews');
    const recommendations = document.querySelector('.product-page .related-products');

    if (!reviewWidget || !recommendations) return false;

    if (reviewWidget.nextElementSibling !== recommendations) {
      recommendations.before(reviewWidget);
    }

    return true;
  };

  if (moveReviewsBeforeRecommendations()) return;

  const productContent = document.getElementById('MainContent');
  if (!productContent) return;

  const observer = new MutationObserver(() => {
    if (moveReviewsBeforeRecommendations()) observer.disconnect();
  });

  observer.observe(productContent, { childList: true, subtree: true });

  window.setTimeout(() => observer.disconnect(), 15000);
})();
